import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useTimesheetMutations } from '@/hooks/timesheets';

interface ManualEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Returns current local datetime in YYYY-MM-DDTHH:MM format for datetime-local inputs */
const getLocalDateTimeMax = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function ManualEntryDialog({ open, onOpenChange }: ManualEntryDialogProps) {
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [reason, setReason] = useState('');
  const { createManualEntry } = useTimesheetMutations();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedReason = reason.trim();
    
    if (!clockIn || !clockOut || !trimmedReason) {
      return;
    }

    const clockInDate = new Date(clockIn);
    const clockOutDate = new Date(clockOut);

    if (clockInDate >= clockOutDate) {
      return;
    }

    createManualEntry.mutate(
      {
        clockIn,
        clockOut,
        reason: trimmedReason,
      },
      {
        onSuccess: () => {
          setClockIn('');
          setClockOut('');
          setReason('');
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Manual Time Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="clockIn">Clock In *</Label>
              <Input
                id="clockIn"
                type="datetime-local"
                value={clockIn}
                onChange={(e) => setClockIn(e.target.value)}
                max={getLocalDateTimeMax()}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="clockOut">Clock Out *</Label>
              <Input
                id="clockOut"
                type="datetime-local"
                value={clockOut}
                onChange={(e) => setClockOut(e.target.value)}
                max={getLocalDateTimeMax()}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reason">Reason *</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for manual entry..."
                rows={3}
                maxLength={500}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createManualEntry.isPending}>
              {createManualEntry.isPending ? 'Creating...' : 'Create Entry'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
